import { EChartsOption } from 'echarts';

import { PowerDetailsSeries } from '../../model';
import { getStyleProp, telemetryStyles } from '../../styles.utils';

const adapterSeriesToAutomationElements = (
  series: PowerDetailsSeries[],
  minTh: number,
  maxTh: number,
): PowerDetailsSeries[] => series.map((s) => ({
  ...s,
  // only in automation graph i need to show the battery percentage as green line
  color: s.name === 'BP' ? getStyleProp('--green') : s.color,
})).concat([{
  name: 'maxTh',
  color: getStyleProp('--red-400'),
  values: series[0].values ? series[0].values.map(([ts, _]) => [ts, maxTh]) : [],
}, {
  name: 'minTh',
  color: getStyleProp('--red-400'),
  values: series[0].values ? series[0].values.map(([ts, _]) => [ts, minTh]) : [],
}]);

export const buildPowerAutomationGraph = (
  series: PowerDetailsSeries[],
  max: number,
  tz: string,
  minTh: number = -220,
  maxTh: number = 200,
): EChartsOption => ({
  legend: {
    show: false,
    selected: {
      P1: true,
      P2: true,
      P3: true,
      P4: true,
      P5: true,
      P6: true,
      BP: true,
      maxTh: true,
      minTh: true,
    },
  },
  tooltip: {
    confine: true,
    trigger: 'axis',
    show: true,
    formatter: (elements: any): string => {
      let template = '';
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let index = 0; index < elements.length; index++) {
        const element = elements[index];
        const { seriesName, data: [_, valuey] } = element;
        const { name, color } = telemetryStyles(seriesName);
        if (['maxTh', 'minTh'].includes(seriesName) === false) {
          template += `
          <div
            class="fx-row ${color} fx-align-center fx-justify-between"
            style="margin-block: 2px; line-height: 13px;">
            <div class="body-3 mr-3">${name}</div>
            <div class="body-3">${
              seriesName !== 'BP'
                ? (Math.abs(valuey / 1000).toFixed(2) + ' kW')
                : (valuey + ' %')
            }</div>
          </div>`;
        }
      }
      return template;
    },
    padding: [0, 2],
    backgroundColor: getStyleProp('--grey-100'),
    borderColor: getStyleProp('--grey-100'),
    borderRadius: 4,
    textStyle: {
      color: getStyleProp('--white'),
    },
  },
  width: '100%',
  height: '100%',
  grid: {
    left: '0%',
    right: '0%',
    top: '0%',
    bottom: '0%',
  },
  xAxis: {
    show: false,
    min: series[0].values ? Math.min(...(series[0].values || [0, 0]).map(([ts, _]) => ts + (Number(tz) / 4) * 60 * 60)) : 0,
    max: series[0].values ? Math.max(...(series[0].values || [0, 0]).map(([ts, _]) => ts + (Number(tz) / 4) * 60 * 60)) : 0,
    type: 'value',
    axisLine: {
      onZero: true,
      show: false,
    },
    axisTick: {
      show: false,
    },
    splitLine: {
      show: false,
    },
  },
  yAxis: {
    min: series[0].name === 'BP'
      ? 0
      :  ['P1', 'P4'].includes(series[0].name)
        ? -100
        : -(Math.max(max, Math.abs(minTh)) + Math.max(max, Math.abs(minTh)) * 0.1),
    max: Math.max(max, maxTh) + Math.max(max, maxTh) * 0.1,
    type: 'value',
    show: false,
  },
  series: adapterSeriesToAutomationElements(series, minTh, maxTh).map((s) => ({
    name: s.name,
    type: 'line',
    smooth: true,
    color: s.color,
    areaStyle: ['minTh', 'maxTh'].includes(s.name) ? {
      origin: s.name === 'minTh' ? 'start' : 'end',
      opacity: 0.1,
    } : undefined,
    lineStyle: {
      width: 1,
      color: ['minTh', 'maxTh'].includes(s.name) ? s.color : undefined,
    },
    symbol: 'none',
    data: s.values?.map(([time, value]) => ([time + (Number(tz) / 4) * 60 * 60, value])),
  })),
});
